import {
  dropoffProviderType,
  type DropoffProviderType,
} from "@redotech/redo-return-core-common/dropoff-provider";
import { MerchantCarrierReturnLabelMode } from "@redotech/redo-return-settings-common/merchant-carrier-return-label-schema";

export const RETURN_RATE_SOURCE_TYPE = {
  REDO_RATES: "redo_rates",
  MERCHANT_CARRIER_ACCOUNTS: "merchant_carrier_accounts",
  MERCHANT_CARRIER_ACCOUNT: "merchant_carrier_account",
  DROPOFF_PROVIDER: "dropoff_provider",
} as const;

export const ReturnRateSourceType = Object.values(RETURN_RATE_SOURCE_TYPE);
export type ReturnRateSourceType = (typeof ReturnRateSourceType)[number];

export interface RedoRatesSource {
  type: typeof RETURN_RATE_SOURCE_TYPE.REDO_RATES;
}

export interface AllMerchantCarrierAccountsRateSource {
  type: typeof RETURN_RATE_SOURCE_TYPE.MERCHANT_CARRIER_ACCOUNTS;
}

export interface MerchantCarrierAccountRateSource {
  type: typeof RETURN_RATE_SOURCE_TYPE.MERCHANT_CARRIER_ACCOUNT;
  carrierAccountId: string;
  /** Service levels the merchant blocked for this account; absent = none blocked. */
  deniedServiceLevels?: string[];
}

export interface DropoffProviderRateSource {
  type: typeof RETURN_RATE_SOURCE_TYPE.DROPOFF_PROVIDER;
  provider: DropoffProviderType;
}

export type ReturnRateSource =
  | RedoRatesSource
  | AllMerchantCarrierAccountsRateSource
  | MerchantCarrierAccountRateSource
  | DropoffProviderRateSource;

export interface ReturnRateSourceSelection {
  sources: ReturnRateSource[];
}

export interface ReturnRateSourceSelectionSummary {
  selectionKey: string;
  sourceCount: number;
  redoRatesSelected: boolean;
  allMerchantCarrierAccountsSelected: boolean;
  merchantCarrierAccountIds: string[];
  merchantCarrierAccountDeniedServiceLevels: Record<string, string[]>;
  dropoffProviders: DropoffProviderType[];
}

export function defaultReturnRateSourceSelection(): ReturnRateSourceSelection {
  return { sources: [{ type: RETURN_RATE_SOURCE_TYPE.REDO_RATES }] };
}

const dropoffProviderTypeSet = new Set<string>(dropoffProviderType);

export function isDropoffProviderType(
  value: unknown,
): value is DropoffProviderType {
  return typeof value === "string" && dropoffProviderTypeSet.has(value);
}

export function isReturnRateSourceSelectionEnabled(team: {
  settings?: {
    returns?: {
      rateSourceSelection?: { enabled?: boolean | null } | null;
    } | null;
  } | null;
}): boolean {
  return !!team.settings?.returns?.rateSourceSelection?.enabled;
}

export function resolveReturnRateSourceSelection({
  team,
  rateSourceSelection,
}: {
  team: {
    settings?: {
      returns?: {
        rateSourceSelection?: { enabled?: boolean | null } | null;
      } | null;
    } | null;
  };
  rateSourceSelection?: ReturnRateSourceSelection;
}): ReturnRateSourceSelection | undefined {
  if (!isReturnRateSourceSelectionEnabled(team)) {
    return undefined;
  }
  return rateSourceSelection;
}

export function canUseMerchantCarrierAccountsForReturnLabels(team: {
  settings?: {
    returns?: {
      merchantCarrierReturnLabels?: {
        mode?: MerchantCarrierReturnLabelMode | null;
      } | null;
    } | null;
  } | null;
}): boolean {
  const mode = team.settings?.returns?.merchantCarrierReturnLabels?.mode;
  return !!mode && mode !== MerchantCarrierReturnLabelMode.ONLY_REDO_CARRIERS;
}

/** Identifies the source itself (ignores deniedServiceLevels); use for matching sources to accounts. */
export function getReturnRateSourceIdentityKey(
  source: ReturnRateSource,
): string {
  switch (source.type) {
    case RETURN_RATE_SOURCE_TYPE.REDO_RATES:
      return source.type;
    case RETURN_RATE_SOURCE_TYPE.MERCHANT_CARRIER_ACCOUNTS:
      return source.type;
    case RETURN_RATE_SOURCE_TYPE.MERCHANT_CARRIER_ACCOUNT:
      return `${source.type}:${source.carrierAccountId}`;
    case RETURN_RATE_SOURCE_TYPE.DROPOFF_PROVIDER:
      return `${source.type}:${source.provider}`;
  }
}

/** Full configuration key (includes deniedServiceLevels); use for caching and label grouping. */
export function getReturnRateSourceKey(source: ReturnRateSource): string {
  const identityKey = getReturnRateSourceIdentityKey(source);
  if (
    source.type === RETURN_RATE_SOURCE_TYPE.MERCHANT_CARRIER_ACCOUNT &&
    source.deniedServiceLevels?.length
  ) {
    return `${identityKey}:deny:${[...source.deniedServiceLevels].sort().join(",")}`;
  }
  return identityKey;
}

export function getReturnRateSourceSelectionKey(
  selection?: ReturnRateSourceSelection,
): string {
  if (!selection) {
    return "";
  }
  return selection.sources.map(getReturnRateSourceKey).sort().join("|");
}

export function hasRedoRatesSource(
  selection?: ReturnRateSourceSelection,
): boolean {
  return (
    !selection ||
    selection.sources.some(
      (source) => source.type === RETURN_RATE_SOURCE_TYPE.REDO_RATES,
    )
  );
}

export function hasAllMerchantCarrierAccountsSource(
  selection?: ReturnRateSourceSelection,
): boolean {
  return (
    !selection ||
    selection.sources.some(
      (source) =>
        source.type === RETURN_RATE_SOURCE_TYPE.MERCHANT_CARRIER_ACCOUNTS,
    )
  );
}

export function getSelectedMerchantCarrierAccountIds(
  selection?: ReturnRateSourceSelection,
): Set<string> | undefined {
  if (!selection || hasAllMerchantCarrierAccountsSource(selection)) {
    return undefined;
  }

  return new Set(
    selection.sources.flatMap((source) =>
      source.type === RETURN_RATE_SOURCE_TYPE.MERCHANT_CARRIER_ACCOUNT
        ? [source.carrierAccountId]
        : [],
    ),
  );
}

/** Account id -> denied service levels; accounts absent from the map have nothing blocked. */
export function getMerchantCarrierAccountDeniedServiceLevels(
  selection?: ReturnRateSourceSelection,
): Map<string, Set<string>> {
  const denied = new Map<string, Set<string>>();
  for (const source of selection?.sources ?? []) {
    if (
      source.type === RETURN_RATE_SOURCE_TYPE.MERCHANT_CARRIER_ACCOUNT &&
      source.deniedServiceLevels?.length
    ) {
      denied.set(source.carrierAccountId, new Set(source.deniedServiceLevels));
    }
  }
  return denied;
}

export function hasSelectedMerchantCarrierAccounts(
  selection?: ReturnRateSourceSelection,
): boolean {
  if (hasAllMerchantCarrierAccountsSource(selection)) {
    return true;
  }
  return (getSelectedMerchantCarrierAccountIds(selection)?.size ?? 0) > 0;
}

export function getSelectedDropoffProviders(
  selection?: ReturnRateSourceSelection,
): Set<DropoffProviderType> | undefined {
  if (!selection) {
    return undefined;
  }

  return new Set(
    selection.sources.flatMap((source) =>
      source.type === RETURN_RATE_SOURCE_TYPE.DROPOFF_PROVIDER
        ? [source.provider]
        : [],
    ),
  );
}

export function getReturnRateSourceSelectionSummary(
  selection: ReturnRateSourceSelection,
): ReturnRateSourceSelectionSummary {
  return {
    selectionKey: getReturnRateSourceSelectionKey(selection),
    sourceCount: selection.sources.length,
    redoRatesSelected: hasRedoRatesSource(selection),
    allMerchantCarrierAccountsSelected:
      hasAllMerchantCarrierAccountsSource(selection),
    merchantCarrierAccountIds: [
      ...(getSelectedMerchantCarrierAccountIds(selection) ?? []),
    ].sort(),
    merchantCarrierAccountDeniedServiceLevels: Object.fromEntries(
      [...getMerchantCarrierAccountDeniedServiceLevels(selection)].map(
        ([accountId, serviceLevels]) => [accountId, [...serviceLevels].sort()],
      ),
    ),
    dropoffProviders: [
      ...(getSelectedDropoffProviders(selection) ?? []),
    ].sort(),
  };
}

export function hasLabelRateSources(
  selection?: ReturnRateSourceSelection,
): boolean {
  return (
    hasRedoRatesSource(selection) ||
    hasSelectedMerchantCarrierAccounts(selection)
  );
}

export function hasDropoffProviderSources(
  selection?: ReturnRateSourceSelection,
): boolean {
  return (
    !selection ||
    selection.sources.some(
      (source) => source.type === RETURN_RATE_SOURCE_TYPE.DROPOFF_PROVIDER,
    )
  );
}

export function shouldIncludeDropoffProviderForRateSourceSelection(
  selection: ReturnRateSourceSelection | undefined,
  provider: DropoffProviderType,
): boolean {
  return (
    !selection ||
    selection.sources.some(
      (source) =>
        source.type === RETURN_RATE_SOURCE_TYPE.DROPOFF_PROVIDER &&
        source.provider === provider,
    )
  );
}
